import { computed, makeObservable } from "mobx";

import { BaseNodeModel } from "@/models/nodes/BaseNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { TextVisitor } from "@/models/nodes/visitors/TextVisitor";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel, NodeId } from "@/models/nodes/types";
import type { VisitableNode, NodeVisitor } from "@/models/nodes/visitors/NodeVisitor";
import type { LabelNodePosition, LabelNodeProps, LabelNodeConstructorProps } from "@corca/data";

export class LabelNodeModel extends BaseNodeModel<LabelNodeProps> implements VisitableNode {
  static type = "label" as const;

  type = LabelNodeModel.type;

  get isNested() {
    return true;
  }

  protected fillRequiredProps(props: LabelNodeConstructorProps): LabelNodeProps {
    this.reportMissingProps(props, ["position"]);
    return { ...props, position: props.position ?? "right-top" };
  }

  constructor(...args: ConstructorParameters<typeof BaseNodeModel<LabelNodeConstructorProps>>) {
    super(...args);
    makeObservable(this, {
      inlineNode: true,
      textContent: true,
    });
  }

  static create(
    doc: DocumentModel,
    props: LabelNodeConstructorProps,
    nodes: NodeModel[] = [],
    id?: NodeId,
  ) {
    if (nodes[0] === undefined) {
      nodes.push(doc.create(InlineNodeModel));
    }

    return new LabelNodeModel(doc, id, props, nodes);
  }

  get inlineNode() {
    return this.children.find((n) => n.is(InlineNodeModel));
  }

  get labelSymbol(): string {
    switch (this.position) {
      case "right-top":
        return "^";
      case "right-bottom":
        return "_";
      default:
        return "";
    }
  }

  get position(): LabelNodePosition {
    return this.props.position;
  }

  get name(): string | undefined {
    return this.props.name;
  }

  get isEmpty(): boolean {
    return this.inlineNode?.nodes.length === 0;
  }

  accept<R>(visitor: NodeVisitor<R>): R {
    return visitor.visitLabelNode(this);
  }

  get textContent() {
    return computed(
      () => {
        return this.inlineNode?.accept(new TextVisitor()) ?? "";
      },
      { name: "LabelNodeModel.textContent" },
    ).get();
  }
}
